// ── OpportunitiesPage — sales deals, grouped by stage with subtotals ──
import { useState, useEffect } from "react";
import { B } from "../../../shared/theme";
import { localFilter, exportCSV, fmt, fmtDate } from "../../../shared/format";
import { Btn, Card, ErrorMsg, ExportBtn, Icon, STh, SearchBar, SectionHeader, Spinner, Td, TdNum, Th } from "../../../shared/ui";
import { useSortable } from "../../../shared/hooks";
import { useApp } from "../../../shared/appContext";

export const OpportunitiesPage = () => {
  const { api, setModal, role, setConfirm, delRec } = useApp();
  const [rows, setRows] = useState([]); const [loading, setLoading] = useState(true); const [err, setErr] = useState(null);
  const [stages, setStages] = useState([]);
  const [q, setQ] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  // One toggle instead of a row of eleven checkboxes. On by default it drops
  // the three groups nobody works day to day: Hold, anything won, anything
  // lost. view.open (arriving from the dashboard's open-deals card) is now the
  // resting state rather than a special case.
  const [hideParked, setHideParked] = useState(true);

  const load = () => { setLoading(true); api.get("getOpportunities").then(d => { setRows(Array.isArray(d) ? d : []); setLoading(false); }).catch(e => { setErr(e.message); setLoading(false); }); };
  useEffect(() => {
    load();
    api.get("getOpportunityStages").then(d => setStages(Array.isArray(d) ? d : [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const commit = (x) => parseFloat(x.commitment_amount || 0);
  const weighted = (x) => commit(x) * (parseFloat(x.probability != null ? x.probability : x.stage_probability || 0) / 100);
  const sum = (arr, fn) => arr.reduce((s, x) => s + fn(x), 0);

  // Stages parked by default: Hold, plus anything the lookup marks closed
  // (Funded/Won and Declined/Lost both carry is_closed). Reading the flag
  // rather than matching names means renaming a stage in SQL doesn't quietly
  // un-park it; the name check is only a fallback for a stage the lookup
  // hasn't got.
  const isParked = (st) => {
    const row = stages.find(s => s.stage === st);
    if (row) return !!(row.is_closed || row.is_won) || st === "Hold";
    const n = String(st || "").toLowerCase();
    return n === "hold" || n.includes("won") || n.includes("funded")
        || n.includes("lost") || n.includes("declined");
  };
  const parkedNames = [...new Set(rows.map(o => o.stage).filter(Boolean))].filter(isParked);

  const searched = localFilter(rows, q, ["name", "companyname", "accountnumber", "ownerName", "stage", "referral_source_name"]);
  const { sorted, sortKey, sortDir, toggle } = useSortable(searched, 'commitment_amount', 'desc');

  const staged = stageFilter ? sorted.filter(o => (o.stage || "") === stageFilter) : sorted;
  // Choosing a parked stage from the dropdown overrides the toggle, otherwise
  // picking "Hold" would show an empty table and read as a bug.
  const parkedHidden = hideParked && !(stageFilter && isParked(stageFilter));
  const filteredRows = parkedHidden ? staged.filter(o => !isParked(o.stage)) : staged;
  const parkedCount = staged.length - filteredRows.length;

  // Stage options and section order both come from the ordered lookup, with any
  // stage present only in the data appended so nothing disappears.
  const stageOpts = [
    ...stages.map(s => s.stage),
    ...[...new Set(rows.map(o => o.stage).filter(Boolean))].filter(st => !stages.some(s => s.stage === st)).sort(),
  ];

  const groups = stageOpts
    .map(st => ({ stage: st, rows: filteredRows.filter(o => (o.stage || "") === st) }))
    .filter(g => g.rows.length > 0);

  if (loading) return <Spinner />; if (err) return <ErrorMsg msg={err} onRetry={load} />;

  const subNum = { textAlign: "right", padding: "8px 12px", fontSize: 12, fontWeight: 600, color: B.cyanDark, fontVariantNumeric: "tabular-nums" };
  const grandNum = { textAlign: "right", padding: "10px 12px", fontSize: 13, fontWeight: 700, color: B.cyanDark, fontVariantNumeric: "tabular-nums" };

  return <>
    <SectionHeader actions={[
      <SearchBar key="s" value={q} onChange={v => setQ(v)} placeholder="Opportunity, account, owner..." />,
      <select key="stage" value={stageFilter} onChange={e => setStageFilter(e.target.value)}
        style={{ padding: "8px 10px", borderRadius: 8, fontSize: 13, cursor: "pointer",
                 border: "1px solid " + (stageFilter ? B.cyan : "var(--color-border-secondary)"),
                 background: "var(--color-background-primary)", color: "var(--color-text-primary)" }}>
        <option value="">All Stages</option>
        {stageOpts.map(st => <option key={st} value={st}>{st}</option>)}
      </select>,
      <label key="parked" title={parkedNames.length ? "Hides: " + parkedNames.join(", ") : "Nothing to hide"}
        style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" }}>
        <input type="checkbox" checked={hideParked} onChange={e => setHideParked(e.target.checked)} />
        Hide held &amp; closed{parkedHidden && parkedCount ? ` (${parkedCount})` : ""}
      </label>,
      // No "+ Add Opportunity": opportunities are created from an account
      // (Sales account → Opportunities) or by converting a lead.
      <ExportBtn key="ex" onClick={() => exportCSV(filteredRows, ["name", "companyname", "stage", "ownerName", "commitment_amount", "probability", "expected_close_date"], "opportunities.csv")}>Export CSV</ExportBtn>
    ]} />

    <Card style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap" }}>
        <thead><tr>
          <STh label="Opportunity" sortKey="name"                currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Account"     sortKey="companyname"         currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Owner"       sortKey="ownerName"           currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Commitment"  sortKey="commitment_amount"   currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
          <STh label="Weighted"    sortKey="probability"         currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
          <STh label="Exp Close"   sortKey="expected_close_date" currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <Th></Th>
        </tr></thead>
        <tbody>
          {groups.length === 0 && <tr><td colSpan={7} style={{ padding: 16, textAlign: "center", color: "var(--color-text-secondary)", fontSize: 13 }}>No opportunities.</td></tr>}
          {groups.map(g => [
            <tr key={"h-" + g.stage}>
              <td colSpan={7} style={{ padding: "8px 12px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: B.cyanDark, background: B.cyanLight }}>
                {g.stage} <span style={{ fontWeight: 600 }}>· {g.rows.length}</span>
              </td>
            </tr>,
            ...g.rows.map(x => (
              <tr key={x.id} style={{ cursor: "pointer" }} onClick={() => setModal({ type: "opp-view", rec: x, accountid: x.accountid, onDone: load })} onMouseEnter={e => e.currentTarget.style.background = "var(--color-background-secondary)"} onMouseLeave={e => e.currentTarget.style.background = ""}>
                <Td style={{ fontWeight: 500 }}>{x.name || "—"}</Td>
                <Td>{x.companyname || "—"}</Td>
                <Td>{x.ownerName || "—"}</Td>
                <TdNum>{x.commitment_amount != null ? fmt(x.commitment_amount) : "—"}</TdNum>
                <TdNum>{fmt(weighted(x))}</TdNum>
                <Td>{x.expected_close_date ? fmtDate(x.expected_close_date) : "—"}</Td>
                <Td><div style={{ display: "flex", gap: 6, justifyContent:"flex-end" }} onClick={e => e.stopPropagation()}>
                  <Btn onClick={() => setModal({ type: "opp-view", rec: x, accountid: x.accountid, onDone: load })}><Icon type="view" /></Btn>
                  {role.canEdit && <Btn onClick={() => setModal({ type: "opp-edit", rec: x, accountid: x.accountid, onDone: load })}><Icon type="edit" /></Btn>}
                  {role.canDelete && <Btn onClick={() => setConfirm({ msg: "Delete this opportunity?", onConfirm: () => delRec("Opportunities", x.id, "Deleted opportunity", load) })} variant="danger"><Icon type="delete" /></Btn>}
                </div></Td>
              </tr>
            )),
            <tr key={"s-" + g.stage} style={{ background: "var(--color-background-secondary)", borderTop: "1.5px solid " + B.cyanLight }}>
              <td style={{ padding: "8px 12px", fontSize: 12, fontWeight: 600, color: B.cyanDark }} colSpan={3}>{g.stage} Subtotal ({g.rows.length})</td>
              <td style={subNum}>{fmt(sum(g.rows, commit))}</td>
              <td style={subNum}>{fmt(sum(g.rows, weighted))}</td>
              <td colSpan={2} />
            </tr>,
          ])}
        </tbody>
        {groups.length > 0 && (
          <tfoot>
            <tr style={{ background: B.cyanLight, borderTop: "2px solid " + B.cyan }}>
              <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, color: B.cyanDark }} colSpan={3}>Grand Total ({filteredRows.length})</td>
              <td style={grandNum}>{fmt(sum(filteredRows, commit))}</td>
              <td style={grandNum}>{fmt(sum(filteredRows, weighted))}</td>
              <td colSpan={2} />
            </tr>
          </tfoot>
        )}
      </table>
    </Card>
  </>;
};
